/**
 * PDP hero (v4) behavior: media carousel + thumbnails, variant radio sync,
 * FrontRow badge mounting and the B2B add-to-cart path.
 *
 * Boots once per `[data-rtnu-pdp-hero]` section. The section passes its ids
 * through data attributes:
 *   data-section-id      Shopify section id (carousel/thumbnail node ids)
 *   data-form-id         product form id
 *   data-currency-symbol currency symbol for per-stick pricing
 *   data-b2b             present when the customer is B2B
 *
 * The buy box itself lives in assets/rtnu-buybox.js.
 */
(() => {
  if (window.__rtnuPdpHeroBound) return;
  window.__rtnuPdpHeroBound = true;

  const CAROUSEL_OPTIONS = { align: 'start', containScroll: 'trimSnaps', slidesToScroll: 1 };
  const isDesktop = () => window.innerWidth > 768;

  const initCarousel = (section, sectionId) => {
    const mainNode = section.querySelector(`#pdp-main-carousel-${sectionId}`);
    const thumbsNode = section.querySelector(`#pdp-thumbnails-${sectionId}`);
    const viewport = mainNode?.querySelector('.embla__viewport');
    if (!mainNode || !viewport || typeof EmblaCarousel !== 'function') return null;

    const dots = mainNode.querySelector('.embla__dots');
    const main = EmblaCarousel(viewport, CAROUSEL_OPTIONS);
    mainNode._emblaApi = main;

    let thumbs = null;
    const mountThumbs = () => {
      const thumbsViewport = thumbsNode?.querySelector('.embla__viewport');
      if (!thumbsViewport || thumbs) return;
      thumbs = EmblaCarousel(thumbsViewport, CAROUSEL_OPTIONS);
      thumbsNode.querySelectorAll('.embla__slide').forEach((slide, index) => {
        slide.addEventListener('click', () => main.scrollTo(index));
      });
    };

    const createDots = () => {
      if (!dots) return;
      dots.replaceChildren(
        ...main.scrollSnapList().map((_, i) => {
          const dot = document.createElement('button');
          dot.className = 'embla__dot';
          dot.type = 'button';
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => main.scrollTo(i));
          return dot;
        })
      );
    };

    const sync = () => {
      const selected = main.selectedScrollSnap();
      dots?.querySelectorAll('.embla__dot').forEach((dot, i) => dot.classList.toggle('is-selected', i === selected));
      if (thumbs) {
        thumbsNode.querySelectorAll('.embla__slide').forEach((slide, i) => slide.classList.toggle('is-selected', i === selected));
        thumbs.scrollTo(selected);
      }
    };

    if (isDesktop()) mountThumbs();
    main.on('select', sync);
    main.on('init', () => {
      createDots();
      sync();
    });

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        main.reInit();
        if (isDesktop()) {
          mountThumbs();
        } else if (thumbs) {
          thumbs.destroy();
          thumbs = null;
        }
        createDots();
        sync();
      }, 150);
    };
    window.addEventListener('resize', onResize);

    mainNode._emblaCleanup = () => {
      window.removeEventListener('resize', onResize);
      main.destroy();
      thumbs?.destroy();
    };

    return main;
  };

  const mountFrontrowBadge = (section) => {
    const badge = section.querySelector('#frontrow-badge');
    const mobileMount = section.querySelector('[data-frontrow-mount="mobile"]');
    const desktopMount = section.querySelector('[data-frontrow-mount="desktop"]');
    if (!badge || !mobileMount || !desktopMount) return;

    const query = window.matchMedia('(max-width: 768px)');
    const mount = () => {
      const target = query.matches ? mobileMount : desktopMount;
      if (badge.parentElement !== target) target.appendChild(badge);
    };
    mount();
    query.addEventListener('change', mount);
  };

  const updateGiveawayCounts = (form, buyContainer, subscriptionCents, onetimeCents) => {
    const isSubscription = form.querySelector('[data-rtnu-selling-plan][name="selling_plan"]') !== null;
    const priceCents = isSubscription ? subscriptionCents : onetimeCents;

    const bonus = buyContainer?.querySelector('.rtnu-product__bonus-entries');
    if (bonus) {
      bonus.dataset.onetimePrice = onetimeCents;
      bonus.dataset.subscriptionPrice = subscriptionCents;
      const multiplier = parseFloat(bonus.dataset.entriesMultiplier) || 10;
      const count = bonus.querySelector('.rtnu-product__bonus-entries-count');
      if (count) count.textContent = Math.round((priceCents / 100) * multiplier);
    }

    const badge = document.querySelector('.rtnu-product__pdp-badge');
    if (badge) {
      badge.dataset.onetimePrice = onetimeCents;
      badge.dataset.subscriptionPrice = subscriptionCents;
      const multiplier = parseFloat(badge.dataset.entriesMultiplier) || 10;
      const count = badge.querySelector('.rtnu-product__pdp-badge-count');
      if (count) count.textContent = `+${Math.round((priceCents / 100) * multiplier)}`;
    }
  };

  const initVariants = (section, form, sectionId, currencySymbol) => {
    const buyContainer = section.querySelector('#buy');
    const productId = form.querySelector('[name="id"]');
    const slides = section.querySelectorAll(`#pdp-main-carousel-${sectionId} .embla__slide`);

    form.querySelectorAll('.rtnu-product__variants .rtnu-option').forEach((option) => {
      const radio = option.querySelector('input[type="radio"]');
      const singlePrice = option.querySelector('.rtnu-product__form-item-single-cost-price');
      if (!radio) return;

      // Per-stick price for pouch products.
      const { priceRaw, itemCount } = radio.dataset;
      if (singlePrice && itemCount) {
        singlePrice.textContent = `${currencySymbol}${(priceRaw / itemCount).toFixed(2)}`;
      }

      option.addEventListener('change', (event) => {
        const { value: variantId, dataset } = event.target;
        if (productId) productId.value = variantId;

        slides.forEach((slide, index) => {
          if (slide.dataset.variant === variantId) {
            section.querySelector(`#pdp-main-carousel-${sectionId}`)?._emblaApi?.scrollTo(index);
          }
        });

        updateGiveawayCounts(
          form,
          buyContainer,
          parseFloat(dataset.limitedSubscriptionCents) || 0,
          parseFloat(dataset.onetimeCents) || 0
        );
      });
    });
  };

  const initB2bAddToCart = (form) => {
    const button = form.querySelector('.rtnu-product__form-submit');
    if (!button) return;

    button.addEventListener('click', async (event) => {
      event.preventDefault();
      const notice = document.querySelector('.rtnu-product__form-notice');
      const price = form.querySelector('.rtnu-product__form-submit-price');
      const status = form.querySelector('.rtnu-product__form-submit-status');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: form.querySelector('[name="id"]').value, quantity: form.querySelector('input[name="quantity"]').value }],
            sections: 'cart-count',
          }),
        });
        const data = await response.json();

        if (data.status === 422) {
          if (notice) {
            Object.assign(notice.style, {
              marginTop: '16px',
              marginBottom: '24px',
              fontSize: '15px',
              color: '#994E0E',
              textAlign: 'center',
              fontWeight: '600',
            });
            notice.textContent = "You've reached the maximum quantity allowed for this product.";
          }
          return;
        }

        if (price && status) {
          price.style.opacity = '0';
          status.style.opacity = '1';
          setTimeout(() => {
            price.style.opacity = '1';
            status.style.opacity = '0';
          }, 2000);
        }
        document.querySelectorAll('.rtnu-header__cart-count').forEach((count) => {
          count.outerHTML = data.sections['cart-count'];
        });
      } catch (error) {
        console.error('[rtnu-pdp-hero] B2B add to cart failed', error);
      }
    });
  };

  const initHero = (section) => {
    if (section.dataset.rtnuPdpHeroReady) return;
    section.dataset.rtnuPdpHeroReady = 'true';

    const { sectionId, formId, currencySymbol = '$' } = section.dataset;
    const form = section.querySelector(`#${CSS.escape(formId)}`);

    initCarousel(section, sectionId);
    mountFrontrowBadge(section);
    if (form) {
      initVariants(section, form, sectionId, currencySymbol);
      if (section.hasAttribute('data-b2b')) initB2bAddToCart(form);
    }
  };

  const initAll = () => document.querySelectorAll('[data-rtnu-pdp-hero]').forEach(initHero);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
